<script setup lang="ts">
import { computed, onMounted, reactive, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import ConfirmHost from "./components/ConfirmHost.vue";
import AppShell, { type AppShellNavGroup, type AppShellNavItem } from "./layouts/AppShell.vue";
import LoginView from "./views/LoginView.vue";
import { api, type Item } from "./api";
import { allows, useAuth } from "./composables/useAuth";
import { useRealtime } from "./composables/useRealtime";

const router = useRouter();
const route = useRoute();
const auth = useAuth();
const brand = reactive({ name: "任务管理系统", mark: "R", image: "" });
const activeKey = computed(() => String(route.name ?? "dashboard"));

const navPermissions: Record<string, string> = {
  dashboard: "dashboard.read",
  tasks: "task.read",
  events: "event.read",
  risk: "risk.read",
  approvals: "event.read",
  notices: "event.read",
  files: "",
  documents: "file.read",
  payroll: "settings.write",
  reports: "report.read",
  settings: "settings.read"
};
const groups = computed<AppShellNavGroup[]>(() => [
  { label: "工作台", items: [
    { key: "dashboard", label: "风控工作台", icon: "layout-dashboard" },
    { key: "tasks", label: "任务中心", icon: "list-checks" },
    { key: "events", label: "风险事件", icon: "triangle-alert" },
    { key: "risk", label: "风控中心", icon: "shield" }
  ] },
  { label: "审计", items: [
    { key: "approvals", label: "审批", icon: "badge-check" }
  ] },
  { label: "协作", items: [
    { key: "notices", label: "公告中心", icon: "megaphone" },
    { key: "files", label: "文件管理", icon: "folder" },
    { key: "documents", label: "文档中心", icon: "file-text" },
    { key: "payroll", label: "工资发放", icon: "wallet" }
  ] },
  { label: "管理", items: [
    { key: "reports", label: "统计报表", icon: "bar-chart" },
    { key: "settings", label: "系统配置", icon: "settings" }
  ] }
].map(group => ({
  ...group,
  items: group.items.filter(item => allows(auth.state.user, navPermissions[item.key] ?? ""))
})).filter(group => group.items.length));

async function loadBrand() {
  try {
    const settings = await api.get<any>("/catalog");
    const basic = settings.basic ?? {};
    brand.name = basic.systemName || "任务管理系统";
    document.title = brand.name;
    brand.image = basic.logo || "";
    if (basic.icon) {
      let favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
      if (!favicon) {
        favicon = document.createElement("link");
        favicon.rel = "icon";
        document.head.appendChild(favicon);
      }
      favicon.href = basic.icon;
    }
  } catch {}
}
function enterWorkbench() {
  if (route.name !== "dashboard") void router.replace({ name: "dashboard" });
  void loadBrand();
}
async function onSignedIn() {
  enterWorkbench();
}
async function logout() {
  await auth.logout();
  await router.replace("/login");
}
function navigate(item: AppShellNavItem) {
  router.push({ name: item.key });
}
function userAction(action: "profile" | "settings") {
  router.push({ name: action === "profile" ? "me" : "account" });
}
function toggleTheme() {
  document.documentElement.classList.toggle("dark");
  localStorage.setItem("riskops_theme", document.documentElement.classList.contains("dark") ? "dark" : "light");
}
useRealtime(event => {
  let type = "";
  try { type = String(JSON.parse(event.data)?.type ?? ""); } catch {}
  window.dispatchEvent(new CustomEvent("riskops:change", { detail: { type } }));
}, auth.signedIn);
watch(() => auth.signedIn.value, (signed) => {
  if (signed && route.name === "login") enterWorkbench();
});
onMounted(async () => {
  if (localStorage.getItem("riskops_theme") === "dark") document.documentElement.classList.add("dark");
  await auth.restore();
  if (auth.signedIn.value) {
    await loadBrand();
    if (route.name === "login") await router.replace("/dashboard");
  } else if (route.name !== "login") {
    await router.replace("/login");
  }
});

const shellUser = computed(() => {
  const user = auth.state.user as Item | null;
  return {
    name: String(user?.name ?? "Jason"),
    meta: `${String(user?.department || "未分配部门")} · ${String(user?.role || "成员")}`,
    initials: String(user?.name ?? "J").slice(0, 1)
  };
});
</script>

<template>
  <div v-if="!auth.state.ready" class="ro-boot">正在加载 RiskOps…</div>
  <LoginView v-else-if="!auth.signedIn.value" @signed-in="onSignedIn" />
  <AppShell
    v-else
    :groups="groups"
    :active-key="activeKey"
    :brand-name="brand.name"
    :brand-mark="brand.mark"
    :brand-image="brand.image"
    :user="shellUser"
    @navigate="navigate"
    @user-action="userAction"
    @toggle-theme="toggleTheme"
    @logout="logout"
  >
    <RouterView />
  </AppShell>
  <ConfirmHost />
</template>

<style scoped>
.ro-boot{min-height:100vh;display:grid;place-items:center;background:var(--ro-bg);color:var(--ro-text)}
</style>
